import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { useMemo } from 'react';

export interface DateRangeOption {
	label: string;
	value: string;
	days: number | null; // null for "All time"
}

interface DateRangeFilterProps {
	selectedRange: string;
	onRangeChange: (range: string, dateFrom?: string, dateTo?: string) => void;
	className?: string;
}

// Standard date range options
export const DATE_RANGE_OPTIONS: DateRangeOption[] = [
	{ label: 'Past 7 days', value: '7d', days: 7 },
	{ label: 'Past 30 days', value: '30d', days: 30 },
	{ label: 'Past 90 days', value: '90d', days: 90 },
	{ label: 'All time', value: 'all', days: null },
];

/**
 * Calculate date range based on number of days from now
 */
export const calculateDateRange = (
	days: number | null,
): { dateFrom?: string; dateTo?: string } => {
	if (days === null) {
		// All time - no date filters
		return {};
	}

	const now = new Date();
	const dateFrom = new Date(now);
	dateFrom.setDate(now.getDate() - days);

	return {
		dateFrom: dateFrom.toISOString(),
		dateTo: now.toISOString(),
	};
};

/**
 * Reusable date range filter component with preset buttons
 * Follows DRY principle by providing consistent date filtering across traces and evaluations
 */
export const DateRangeFilter = ({
	selectedRange,
	onRangeChange,
	className = '',
}: DateRangeFilterProps) => {
	// Calculate current date range for display
	const currentDateInfo = useMemo(() => {
		const option = DATE_RANGE_OPTIONS.find(
			(opt) => opt.value === selectedRange,
		);
		if (!option) return null;

		if (option.days === null) {
			return { label: option.label, description: 'No date filter applied' };
		}

		const { dateFrom } = calculateDateRange(option.days);
		const fromDate = dateFrom ? new Date(dateFrom).toLocaleDateString() : '';

		return {
			label: option.label,
			description: `From ${fromDate} to today`,
		};
	}, [selectedRange]);

	const handleRangeSelect = (option: DateRangeOption) => {
		const { dateFrom, dateTo } = calculateDateRange(option.days);
		onRangeChange(option.value, dateFrom, dateTo);
	};

	return (
		<div className={`tw-flex tw-items-center tw-gap-2 ${className}`}>
			<Calendar className='tw-h-4 tw-w-4 tw-text-gray-500' />
			<span className='tw-text-sm tw-font-medium tw-text-gray-700'>
				Date range:
			</span>

			{/* Date Range Buttons */}
			<div className='tw-flex tw-gap-1'>
				{DATE_RANGE_OPTIONS.map((option) => (
					<Button
						key={option.value}
						variant={selectedRange === option.value ? 'default' : 'outline'}
						size='sm'
						onClick={() => handleRangeSelect(option)}
						className='tw-text-xs tw-px-3 tw-py-1'
					>
						{option.label}
					</Button>
				))}
			</div>

			{/* Current Selection Info */}
			{currentDateInfo && (
				<div className='tw-ml-2 tw-text-xs tw-text-gray-500 tw-border-l tw-pl-2'>
					<div className='tw-font-medium'>{currentDateInfo.label}</div>
					<div>{currentDateInfo.description}</div>
				</div>
			)}
		</div>
	);
};
